import { useRef, useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, ArrowUpRight, Play, MapPin, Users, Calendar, Star, Clock, Heart, Flower2, TreePine, Egg, Coffee, UtensilsCrossed, Sparkles, Briefcase, PartyPopper, ChevronLeft, ChevronRight, Quote, Award, Check, ChefHat, Mail } from 'lucide-react'
import { getEvents, transformEventForDisplay, type Event } from '../services/eventsService'
import { brunchService, Brunch } from '../services/brunchService'
import { getEventImageUrl, EVENT_IMAGE_FALLBACK } from '../config/api'

// Icon Mapping
const iconMap: Record<string, any> = {
  Heart,
  Flower2,
  TreePine,
  Egg,
  Coffee,
  UtensilsCrossed,
  Sparkles
}

// Daten für die Event-Kategorien
const eventCategories = [
  {
    id: 'hochzeiten',
    title: 'Hochzeiten',
    subtitle: 'Hier beginnt Ihre gemeinsame Zukunft',
    description: 'Historisches Ambiente für den wichtigsten Tag Ihres Lebens.',
    image: '/assets/home_pics/hochzeit_neu_vorschau.jpeg',
    link: '/hochzeiten',
    capacity: 'Bis 170 Gäste'
  },
  {
    id: 'corporate',
    title: 'Firmenfeiern',
    subtitle: 'Wo Geschäft auf Eleganz trifft',
    description: 'Professionelle Events für Teams von 20 bis 2000+ Personen.',
    image: '/assets/home_pics/HK_184.jpg',
    link: '/unternehmensfeiern',
    capacity: 'Bis 2000+ Gäste'
  },
  {
    id: 'private',
    title: 'Private Feiern',
    subtitle: 'Feiern Sie die schönen Momente',
    description: 'Geburtstage, Jubiläen und besondere Anlässe.',
    image: '/assets/home_pics/DS2.JPG',
    link: '/private-feiern',
    capacity: 'Flexibel'
  }
]

// Statistiken
const stats = [
  { number: '25+', label: 'Jahre Erfahrung' },
  { number: '2000+', label: 'Gäste Kapazität' },
  { number: '150+', label: 'Events pro Jahr' },
  { number: '4', label: 'Einzigartige Räume' }
]

// Testimonials
const testimonials = [
  {
    id: 1,
    category: 'Brunch',
    categoryIcon: Coffee,
    name: 'Kathy Rik',
    text: 'Wir waren zum Brunchen am 2. Weihnachtstag dort. Das Ambiente ist sehr schön und gemütlich. Es wirkt rustikal, jedoch stylisch. Der Service ist sehr freundlich und aufmerksam. Das Essen ist köstlich und es wurde immer frisch nachgelegt, sodass man in Ruhe genießen konnte. Das Preis-Leistungsverhältnis ist absolut stimmig. Wir haben ein leckeres und gemütliches Weihnachts-Familien-Treffen dort verbracht und sagen Dankeschön.',
    rating: 5
  },
  {
    id: 2,
    category: 'Firmenfeier',
    categoryIcon: Briefcase,
    name: 'Heinemann DUS',
    text: 'Unsere Firmenfeier hier war ein voller Erfolg – ein Erlebnis, das wir so schnell nicht vergessen werden! Das Essen war köstlich und wurde mit viel Liebe zum Detail zubereitet. Das Team war ausgesprochen herzlich und hat uns jeden Wunsch von den Augen abgelesen. Man fühlt sich direkt wie zu Hause, was in dieser Branche nicht selbstverständlich ist. Die Location selbst ist einladend, rustikal; man merkt, dass viel Wert auf Atmosphäre gelegt wurde. Wir können diesen Ort wärmstens empfehlen!',
    rating: 5
  },
  {
    id: 3,
    category: 'Hochzeit',
    categoryIcon: Heart,
    name: 'Frauke Herbst',
    text: 'Großartige Hochzeit! Wir haben unsere Hochzeit hier gefeiert und alles war wirklich perfekt! Das Essen war super lecker und sah toll aus. Es wurde immer zügig nachgelegt. Selbst das Rindfleisch ging trotz vieler Rindfleischliebhaber aus Brasilien nicht zu Neige! Der Service war super freundlich und schnell. Unsere Gäste waren begeistert. Herr Reese und seine Frau sind auf all unsre Wünsche eingegangen und haben uns mit hilfreichen Tipps unterstützt. Auch die Preiskalkulation stimmte am Ende mit der Anfangskalkulation überein- wichtig für eine gute Planung. Wir würden immer wieder hier feiern und Freunde die unsere Gäste waren und demnächst heiraten werden dies auch tun. Wirklich top!',
    rating: 5
  },
  {
    id: 4,
    category: 'Firmenfeier',
    categoryIcon: Briefcase,
    name: 'Arnica Reiners',
    text: 'Wir feierten dort unser Betriebsfest von Helios. Knapp 1200 Menschen und alles war super organisiert 💯',
    rating: 5
  },
  {
    id: 5,
    category: 'Private Feier',
    categoryIcon: PartyPopper,
    name: 'Bernhard Roth',
    text: 'Ich habe mehrere Familienfeiern in gebuchten Räumen gehabt, kleine Abstecher zum Essen zwischendurch und mehrmals den Sonntagsbrunch genossen und bin rundum mit der Freundlichkeit und der Qualität und dem Preis/Leistungsverhältnis zufrieden. Wir kommen gerne wieder.',
    rating: 5
  },
  {
    id: 6,
    category: 'Party',
    categoryIcon: PartyPopper,
    name: 'D. Küppers',
    text: 'Nach ein paar Jahren Pause wieder "in der Hückels" Halloween feiern gewesen und es war richtig gut. Zwei Floors für alle was da gewesen! Publikum (jung und jungalt 😉) total nett! DJs sehr gut! Personal sehr freundlich! Verhungern kann man auch nicht. Es wurde vor Ort Pizza gebacken. Ich komme definitiv wieder!',
    rating: 5
  },
  {
    id: 7,
    category: 'Brunch',
    categoryIcon: Coffee,
    name: 'Astrid M.',
    text: 'Wir waren heute am 2. Weihnachtstag in HückelsMay. Das Ambiente sehr schön weihnachtlich geschmückt, das Essen war sehr lecker, Service schnell und gut organisiert. Wir hatten einen rundum schönen Brunch und haben uns sehr wohl gefühlt.',
    rating: 5
  },
  {
    id: 8,
    category: 'Firmenfeier',
    categoryIcon: Briefcase,
    name: 'Sven Wiszniewski',
    text: 'Absolut traumhafte Location für Firmenfeiern. Im Innenhof kommt man gemütlich und gesellig zusammen und innen fühlt man sich wohl. Perfekte Mischung aus Hof-, Land und Partyfeeling mit tollem Service und bester Ausstattung. Komme gerne immer wieder und freue mich auf das nächste Mal!',
    rating: 5
  },
  {
    id: 9,
    category: 'Private Feier',
    categoryIcon: Heart,
    name: 'May',
    text: 'Ich war letztens mit meinem Mann hier. Der Service und das Essen wirklich ausgezeichnet! Sehr romantisch, wir kommen auf jeden Fall wieder, vielen Dank!',
    rating: 5
  },
  {
    id: 10,
    category: 'Party',
    categoryIcon: PartyPopper,
    name: 'Tom Z.',
    text: 'Super freundliches Personal, wunderschöne Location und grandiose Partys!',
    rating: 5
  }
]

function Testimonials() {
  const [current, setCurrent] = useState(0)
  const next = () => setCurrent((prev) => (prev >= testimonials.length - 1 ? 0 : prev + 1))
  const prev = () => setCurrent((prev) => (prev <= 0 ? testimonials.length - 1 : prev - 1))

  return (
    <section className="content-auto relative py-20 md:py-28 overflow-hidden border-t border-white/5">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/10 via-transparent to-transparent" />

      <div className="max-w-[1600px] mx-auto px-6 relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-800/10 border border-brand-800/20 rounded-full mb-4">
            <Star className="w-3.5 h-3.5 text-brand-400" />
            <span className="text-brand-400 text-xs uppercase tracking-[0.3em] font-semibold">Kundenstimmen</span>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light mb-3 tracking-tight">
            Was unsere Gäste <span className="italic text-brand-400">erleben</span>
          </h2>
          <p className="text-white/50 text-base max-w-xl mx-auto">
            Echte Erfahrungen von zufriedenen Gästen
          </p>
        </div>

        <div className="relative">
          <button
            type="button"
            onClick={prev}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-xl bg-zinc-900 border border-white/10 hover:border-brand-800/50 flex items-center justify-center transition-transform duration-200 active:scale-95 group shadow-lg -ml-2 md:-ml-4"
            aria-label="Vorherige Bewertungen"
          >
            <ChevronLeft className="w-5 h-5 text-white/60 group-hover:text-brand-400 transition-colors" />
          </button>

          <button
            type="button"
            onClick={next}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-xl bg-zinc-900 border border-white/10 hover:border-brand-800/50 flex items-center justify-center transition-transform duration-200 active:scale-95 group shadow-lg -mr-2 md:-mr-4"
            aria-label="Nächste Bewertungen"
          >
            <ChevronRight className="w-5 h-5 text-white/60 group-hover:text-brand-400 transition-colors" />
          </button>

          <div className="overflow-hidden px-4 md:px-8">
            <div
              className="flex gap-3 sm:gap-4 md:gap-5 transition-transform duration-500 ease-out"
              style={{ transform: `translate3d(calc(-${current} * (260px + 1rem)), 0, 0)` }}
            >
              {testimonials.map((testimonial) => {
                const IconComponent = testimonial.categoryIcon
                return (
                  <div key={testimonial.id} className="w-[280px] sm:w-[300px] md:w-[320px] flex-shrink-0">
                    <div className="relative bg-zinc-900 border border-white/10 rounded-xl overflow-hidden shadow-lg h-full flex flex-col">
                      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-brand-400/50 to-transparent" />
                      <div className="p-4 flex flex-col flex-1">
                        <div className="flex flex-col gap-2 mb-3">
                          <div className="flex items-center gap-2 px-2.5 py-1 bg-brand-800/20 border border-brand-700/20 rounded-lg w-fit">
                            <IconComponent className="w-3 h-3 text-brand-400" />
                            <span className="text-brand-300 text-xs font-medium">{testimonial.category}</span>
                          </div>
                          <div className="flex items-center gap-0.5">
                            {[...Array(testimonial.rating)].map((_, i) => (
                              <Star key={i} className="w-3 h-3 text-brand-400 fill-brand-400" />
                            ))}
                          </div>
                        </div>
                        <blockquote className="relative z-10 text-white/90 text-xs leading-relaxed mb-3 flex-1">
                          <Quote className="w-5 h-5 text-brand-400/20 absolute -top-1 -left-1" />
                          <span className="relative">{testimonial.text}</span>
                        </blockquote>
                        <div className="flex items-center gap-2.5 pt-3 border-t border-white/5">
                          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-brand-800/30 border border-brand-700/20 flex items-center justify-center">
                            <span className="text-brand-400 text-xs font-serif font-semibold">
                              {testimonial.name.charAt(0)}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium text-xs truncate">{testimonial.name}</p>
                            <p className="text-white/40 text-[10px]">Verifizierter Gast</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setCurrent(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === current ? 'w-8 h-2 bg-brand-400' : 'w-2 h-2 bg-white/20 hover:bg-white/40'
                }`}
                aria-label={`Bewertung ${index + 1}`}
              />
            ))}
          </div>
        </div>

        <div className="text-center mt-10">
          <div className="inline-flex items-center gap-2 text-white/40 text-sm">
            <Award className="w-4 h-4 text-brand-400" />
            <span>Über 4500 erfolgreiche Events seit 1998</span>
          </div>
        </div>
      </div>
    </section>
  )
}

function scheduleIdle(task: () => void) {
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(task, { timeout: 2000 })
    return () => window.cancelIdleCallback(id)
  }
  const timer = window.setTimeout(task, 400)
  return () => window.clearTimeout(timer)
}

export default function Home() {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isVideoLoaded, setIsVideoLoaded] = useState(false)
  const [upcomingEvents, setUpcomingEvents] = useState<any[]>([])
  const [loadingEvents, setLoadingEvents] = useState(true)
  const [featuredBrunch, setFeaturedBrunch] = useState<Brunch | null>(null)
  const [nextBrunches, setNextBrunches] = useState<Brunch[]>([])
  const [loadingBrunch, setLoadingBrunch] = useState(true)

  useEffect(() => {
    return scheduleIdle(() => {
      const loadEvents = async () => {
        try {
          setLoadingEvents(true)
          const events = await getEvents()
          const activeEvents = events.filter(
            (event: Event) => event.status === 'Aktiv' || event.status === 'Geplant'
          )
          const sortedEvents = activeEvents.sort((a: Event, b: Event) => {
            return new Date(a.date).getTime() - new Date(b.date).getTime()
          })
          setUpcomingEvents(sortedEvents.slice(0, 4).map(transformEventForDisplay))
        } catch (error) {
          console.error('Fehler beim Laden der Events:', error)
          setUpcomingEvents([])
        } finally {
          setLoadingEvents(false)
        }
      }

      const loadNextBrunches = async () => {
        try {
          setLoadingBrunch(true)
          const brunches = await brunchService.getNextBrunches()
          setNextBrunches(brunches)
          setFeaturedBrunch(brunches.length > 0 ? brunches[0] : null)
        } catch (error) {
          console.error('Fehler beim Laden der nächsten Brunches:', error)
          setNextBrunches([])
          setFeaturedBrunch(null)
        } finally {
          setLoadingBrunch(false)
        }
      }

      loadEvents()
      loadNextBrunches()
    })
  }, [])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    let started = false
    const start = () => {
      if (started) return
      started = true
      video.preload = 'none'
      video.src = '/hueckelsherovideo_komp.mp4'
      const playVideo = () => {
        video.play().then(() => setIsVideoLoaded(true)).catch(() => {})
      }
      video.addEventListener('loadeddata', playVideo, { once: true })
      video.load()
    }
    const timer = window.setTimeout(start, 2000)
    return () => {
      window.clearTimeout(timer)
      video.pause()
    }
  }, [])

  return (
    <div className="bg-[#0a0a0a]">
      
      {/* ════════════════════════════════════════════════════════════
          HERO SECTION - Cinematic Fullscreen
      ════════════════════════════════════════════════════════════ */}
      <section className="relative h-screen w-full overflow-hidden bg-[#0a0a0a]">
        <div className="absolute inset-0">
          <video
            ref={videoRef}
            muted
            loop
            playsInline
            preload="none"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ opacity: isVideoLoaded ? 1 : 0 }}
          />
        </div>

        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-[#0a0a0a]" />
        
        <div className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6">
          <img
            src="/Logo_HM.webp"
            alt="Hückels May"
            width={960}
            height={699}
            fetchPriority="high"
            decoding="async"
            className="h-20 sm:h-24 md:h-32 lg:h-40 w-auto mb-6 md:mb-8 opacity-90"
          />
          
          <p className="text-white/60 text-xs sm:text-sm md:text-base uppercase tracking-[0.4em] mb-4 md:mb-6">
            Eventlocation am Niederrhein
          </p>

          <h1 className="font-serif text-3xl sm:text-4xl md:text-6xl lg:text-8xl text-white font-light tracking-tight leading-[0.9] mb-8 md:mb-12">
            Wo Momente<br />
            <span className="italic text-white/80">unvergesslich</span> werden
          </h1>

          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/kontakt">
              <button
                type="button"
                className="px-6 sm:px-8 md:px-10 py-3 md:py-4 bg-white text-black text-xs uppercase tracking-[0.3em] font-bold rounded-full hover:bg-brand-800 hover:text-white active:scale-[0.98] transition-all duration-200"
              >
                Event anfragen
              </button>
            </Link>
            <Link to="/galerie">
              <button
                type="button"
                className="px-6 sm:px-8 md:px-10 py-3 md:py-4 border border-white/30 text-white text-xs uppercase tracking-[0.3em] font-bold rounded-full hover:bg-white/10 active:scale-[0.98] transition-all duration-200 flex items-center gap-2 md:gap-3"
              >
                <Play className="w-4 h-4" />
                Galerie
              </button>
            </Link>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20">
          <div className="hero-scroll-cue w-6 h-10 border border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-white/60 rounded-full" />
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          INTRO SECTION - Elegant Statement
      ════════════════════════════════════════════════════════════ */}
      <section className="content-auto relative py-24 md:py-40 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-8"
          >
            Seit über 25 Jahren
          </motion.p>
          
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2 }}
            className="font-serif text-2xl sm:text-3xl md:text-5xl lg:text-6xl text-white font-light leading-tight mb-8 md:mb-12"
          >
            Wir verwandeln historische Räume in die 
            <span className="text-white/40"> Kulisse Ihrer besonderen Momente.</span>
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Link to="/geschichte" className="inline-flex items-center gap-3 text-white/50 hover:text-white transition-colors group">
              <span className="text-sm uppercase tracking-[0.2em]">Unsere Geschichte</span>
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          STATS BAR - Impressive Numbers
      ════════════════════════════════════════════════════════════ */}
      <section className="content-auto border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-4">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="py-12 md:py-16 px-6 text-center border-r border-white/5 last:border-r-0"
              >
                <p className="font-serif text-4xl md:text-5xl lg:text-6xl text-white font-light mb-2">{stat.number}</p>
                <p className="text-white/40 text-xs uppercase tracking-[0.2em]">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          EVENT CATEGORIES - Interactive Showcase
      ════════════════════════════════════════════════════════════ */}
      <section className="content-auto relative py-32 md:py-48">
        <div className="max-w-[1600px] mx-auto px-6">
          
          {/* Section Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 md:mb-24">
            <div>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4"
              >
                Ihr Event, Ihre Wahl
              </motion.p>
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="font-serif text-4xl md:text-6xl lg:text-7xl text-white font-light"
              >
                Wofür planen Sie<br />
                <span className="italic text-white/60">Ihr Event?</span>
              </motion.h2>
            </div>
            
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="mt-8 md:mt-0"
            >
              <Link to="/veranstaltungen" className="inline-flex items-center gap-3 text-white/50 hover:text-white transition-colors group">
                <span className="text-sm uppercase tracking-[0.2em]">Alle Events</span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-2" />
              </Link>
            </motion.div>
          </div>

          {/* 3-Spalten Kachel-Layout */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {eventCategories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
              >
                <Link 
                  to={category.link}
                  className="group block h-full"
                >
                  <div className="relative h-full rounded-2xl overflow-hidden border border-white/5 hover:border-white/15 transition-all duration-500 bg-[#111]">
                    {/* Bild */}
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={category.image}
                        alt={category.title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-[#111]/40 to-transparent" />
                      
                      {/* Capacity Badge */}
                      <div className="absolute bottom-4 left-4 px-4 py-2 bg-white/10 backdrop-blur-xl rounded-full border border-white/10">
                        <div className="flex items-center gap-2">
                          <Users className="w-3.5 h-3.5 text-white/60" />
                          <span className="text-white text-xs">{category.capacity}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="text-white/40 text-[10px] uppercase tracking-[0.3em] mb-1.5">{category.subtitle}</p>
                          <h3 className="font-serif text-xl md:text-2xl text-white font-light">{category.title}</h3>
                        </div>
                        <div className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center transition-all duration-500 group-hover:bg-brand-800 group-hover:border-brand-800">
                          <ArrowUpRight className="w-4 h-4 text-white/40 transition-colors group-hover:text-white" />
                        </div>
                      </div>
                      <p className="text-white/40 text-sm leading-relaxed">{category.description}</p>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          KOMMENDE EVENTS - Party Events aus /partys
      ════════════════════════════════════════════════════════════ */}
      <section className="content-auto relative py-32 md:py-48 overflow-hidden">
        {/* Section Header mit Padding */}
        <div className="max-w-[1600px] mx-auto px-6 mb-16 md:mb-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between">
            <div>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4"
              >
                Nicht verpassen
              </motion.p>
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="font-serif text-4xl md:text-6xl lg:text-7xl text-white font-light"
              >
                Kommende Events
              </motion.h2>
            </div>
            
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="mt-8 md:mt-0"
            >
              <Link to="/partys" className="inline-flex items-center gap-3 text-white/50 hover:text-white transition-colors group">
                <span className="text-sm uppercase tracking-[0.2em]">Alle Events</span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-2" />
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Events Grid - Konsistentes Design */}
        <div className="max-w-[1600px] mx-auto px-6">
          {loadingEvents ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="rounded-2xl overflow-hidden bg-[#151515] border border-white/5 animate-pulse">
                  <div className="relative aspect-[4/3] bg-gray-800" />
                  <div className="p-4 md:p-5">
                    <div className="h-4 bg-gray-700 rounded mb-2 w-2/3" />
                    <div className="h-3 bg-gray-700 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : upcomingEvents.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {upcomingEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Link to={`/partys/${event.slug}`} className="group block h-full">
                    <div className="relative h-full rounded-2xl overflow-hidden border border-white/5 hover:border-white/10 transition-all duration-300 bg-[#151515]">
                      {/* Vorschaubild */}
                      <div className="relative aspect-[4/3] overflow-hidden">
                        <img 
                          src={getEventImageUrl(event.imageUrl)} 
                          alt={event.title}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          onError={(e) => {
                            if ((e.currentTarget as HTMLImageElement).dataset.flyerFallback === '1') return
                            ;(e.currentTarget as HTMLImageElement).dataset.flyerFallback = '1'
                            e.currentTarget.src = EVENT_IMAGE_FALLBACK
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-[#111]/40 to-transparent" />
                        
                        {/* Kategorie Badge */}
                        {event.category && (
                          <div className="absolute bottom-4 left-4 px-4 py-2 bg-white/10 backdrop-blur-xl rounded-full border border-white/10">
                            <span className="text-white text-xs font-medium">{event.category}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="p-4 md:p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h3 className="font-serif text-lg md:text-xl text-white font-light mb-2 line-clamp-2 group-hover:text-brand-400 transition-colors">
                              {event.title}
                            </h3>
                          </div>
                          <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-brand-800 transition-colors duration-300 flex-shrink-0 ml-3">
                            <ArrowUpRight className="w-4 h-4 text-white/40 group-hover:text-white transition-colors" />
                          </div>
                        </div>
                        
                        {/* Datum & Zeit */}
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2 text-white/60 text-xs">
                            <Calendar className="w-3.5 h-3.5 text-brand-400" />
                            <span>{event.date}</span>
                          </div>
                          {event.time && (
                            <div className="flex items-center gap-2 text-white/60 text-xs">
                              <Clock className="w-3.5 h-3.5 text-brand-400" />
                              <span>{event.time}</span>
                            </div>
                          )}
                          {event.location && (
                            <div className="flex items-center gap-2 text-white/50 text-xs">
                              <MapPin className="w-3 h-3 text-brand-400" />
                              <span className="truncate">{event.location}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/40 text-sm">Aktuell keine kommenden Events</p>
            </div>
          )}
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          NÄCHSTER BRUNCH - Kompakte Anzeige
      ════════════════════════════════════════════════════════════ */}
      <section className="content-auto py-24 md:py-32 border-t border-white/5">
        <div className="max-w-[1600px] mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 lg:gap-20 items-center">
            
            {/* Left: Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4">Kulinarisches Erlebnis</p>
              <h2 className="font-serif text-4xl md:text-5xl text-white font-light mb-6">
                Nächster Brunch
              </h2>
              <p className="text-white/50 text-lg leading-relaxed mb-8 max-w-lg">
                Genießen Sie unser exquisites Brunch-Buffet in einzigartiger Atmosphäre. 
                Ein kulinarisches Erlebnis für die ganze Familie.
              </p>
              
              {/* Next Brunch Info */}
              {loadingBrunch ? (
                <div className="bg-[#151515] rounded-2xl p-6 border border-white/5 mb-8">
                  <div className="flex items-start gap-5">
                    <div className="w-16 h-16 rounded-xl bg-brand-800/20 flex items-center justify-center flex-shrink-0 animate-pulse">
                      <div className="w-8 h-8 bg-white/10 rounded" />
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-white/10 rounded w-32 animate-pulse" />
                      <div className="h-6 bg-white/10 rounded w-48 animate-pulse" />
                      <div className="h-4 bg-white/10 rounded w-40 animate-pulse" />
                    </div>
                  </div>
                </div>
              ) : nextBrunches.length > 0 ? (
                <div className="space-y-4 mb-8">
                  {nextBrunches.map((brunch, index) => {
                    const Icon = iconMap[brunch.icon_name] || Coffee
                    const brunchUrl = brunch.slug ? `/brunch/${brunch.slug}` : '/brunch'
                    return (
                      <Link
                        key={brunch.id || index}
                        to={brunchUrl}
                        className="group block bg-[#151515] rounded-2xl p-6 border border-white/5 hover:border-white/15 hover:bg-[#1a1a1a] transition-all duration-300"
                      >
                        <div className="flex items-start gap-5">
                          <div className="w-16 h-16 rounded-xl bg-brand-800/20 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-8 h-8 text-brand-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white/40 text-xs uppercase tracking-wider mb-1">
                              {brunch.event_date ? (
                                (() => {
                                  try {
                                    const date = new Date(brunch.event_date)
                                    if (isNaN(date.getTime())) {
                                      return 'Datum folgt'
                                    }
                                    return date.toLocaleDateString('de-DE', { 
                                      day: 'numeric', 
                                      month: 'long', 
                                      year: 'numeric' 
                                    })
                                  } catch (e) {
                                    return 'Datum folgt'
                                  }
                                })()
                              ) : 'Datum folgt'}
                            </p>
                            <h3 className="font-serif text-2xl text-white font-light mb-2">{brunch.title}</h3>
                            <p className="text-white/50 text-sm mb-3">
                              {brunch.event_time || 'Uhrzeit folgt'}
                              {brunch.subtitle && ` · ${brunch.subtitle}`}
                              {brunch.price && ` · ${brunch.price}`}
                            </p>
                            <span className="inline-flex items-center gap-2 text-brand-400 text-sm font-medium group-hover:text-brand-300 transition-colors">
                              Details anzeigen
                              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                            </span>
                          </div>
                        </div>
                      </Link>
                    )
                  })}
                </div>
              ) : null}

              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <Link to="/brunch">
                  <button
                    type="button"
                    className="px-6 md:px-8 py-3 md:py-4 bg-white text-black text-xs uppercase tracking-[0.2em] font-bold rounded-full hover:bg-brand-800 hover:text-white active:scale-[0.98] transition-all duration-200"
                  >
                    Jetzt reservieren
                  </button>
                </Link>
                <Link to="/brunch">
                  <button
                    type="button"
                    className="px-6 md:px-8 py-3 md:py-4 border border-white/20 text-white text-xs uppercase tracking-[0.2em] font-bold rounded-full hover:bg-white/5 active:scale-[0.98] transition-all duration-200"
                  >
                    Alle Termine
                  </button>
                </Link>
              </div>
            </motion.div>

            {/* Right: Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative aspect-[4/3] rounded-3xl overflow-hidden">
                <img 
                  src={nextBrunches.length > 0 
                    ? (nextBrunches[0].preview_image_url || nextBrunches[0].image_url || nextBrunches[0].flyer_image_url || "/assets/brunch/brunch_vorschau_fallback.jpg")
                    : "/assets/brunch/brunch_vorschau_fallback.jpg"
                  }
                  loading="lazy"
                  decoding="async" 
                  alt={nextBrunches.length > 0 ? nextBrunches[0].title : "Brunch Ambiente"}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    if (e.currentTarget.src !== '/assets/brunch/brunch_vorschau_fallback.jpg') {
                      e.currentTarget.src = '/assets/brunch/brunch_vorschau_fallback.jpg'
                    }
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      <Testimonials />

      {/* ════════════════════════════════════════════════════════════
          RÄUME PREVIEW - Visual Teaser
      ════════════════════════════════════════════════════════════ */}
      <section className="content-auto py-32 md:py-48 border-t border-white/5">
        <div className="max-w-[1600px] mx-auto px-6">
          
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16">
            <div>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4"
              >
                Die Location
              </motion.p>
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="font-serif text-4xl md:text-6xl text-white font-light"
              >
                Vier einzigartige Räume
              </motion.h2>
            </div>
            
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="mt-6 md:mt-0"
            >
              <Link to="/raeume" className="inline-flex items-center gap-3 text-white/50 hover:text-white transition-colors group">
                <span className="text-sm uppercase tracking-[0.2em]">Alle Räume</span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-2" />
              </Link>
            </motion.div>
          </div>
          
          {/* Rooms Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {[
              { name: 'Festscheune', slug: 'fest-scheune', image: '/assets/raume/fest.jpg', capacity: '400' },
              { name: 'Weinstube', slug: 'weinstube', image: '/assets/raume/wein.jpg', capacity: '30' },
              { name: 'Landhaus', slug: 'landhaus', image: '/assets/raume/land.jpg', capacity: '100' },
              { name: 'Innenhof', slug: 'innenhof', image: '/assets/home_pics/HK_277.JPG', capacity: '700' }
            ].map((room, index) => (
              <motion.div
                key={room.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link to={`/raeume/${room.slug}`} className="group block">
                  <div className="rounded-2xl overflow-hidden bg-[#151515] border border-white/5 hover:border-white/10 transition-all duration-300">
                    {/* Image */}
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img 
                        src={room.image} 
                        alt={room.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    </div>
                    {/* Text unterhalb des Bildes */}
                    <div className="p-4 md:p-5">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-white text-lg md:text-xl font-serif font-light mb-1">{room.name}</h3>
                          <p className="text-white/40 text-xs">Bis {room.capacity} Gäste</p>
                        </div>
                        <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-brand-800 transition-colors duration-300">
                          <ArrowUpRight className="w-4 h-4 text-white/40 group-hover:text-white transition-colors" />
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          STELLENANZEIGE - Koch gesucht (ab 15. August)
      ════════════════════════════════════════════════════════════ */}
      <section className="content-auto py-16 md:py-20 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">

            {/* Flyer */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex justify-center md:justify-end"
            >
              <a
                href="/assets/kochgesucht.jpeg"
                target="_blank"
                rel="noopener noreferrer"
                className="group block w-[92%] max-w-[440px]"
                aria-label="Stellenanzeige Koch öffnen"
              >
                <div className="relative overflow-hidden rounded-xl border border-white/10 bg-[#111] shadow-lg shadow-black/30">
                  <img
                    src="/assets/kochgesucht.jpeg"
                    alt="Stellenanzeige: Koch gesucht ab 15. August, 70 % Teilzeit"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto transition-transform duration-500 group-hover:scale-[1.02]"
                  />
                </div>
              </a>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-center md:text-left"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-brand-800/10 border border-brand-800/20 rounded-full mb-4">
                <ChefHat className="w-3.5 h-3.5 text-brand-400" />
                <span className="text-brand-400 text-xs uppercase tracking-[0.3em] font-semibold">Jetzt gesucht</span>
              </div>

              <p className="text-white/50 text-xs uppercase tracking-[0.25em] mb-2">
                Wir suchen ab dem 15. August
              </p>

              <h2 className="font-serif text-3xl md:text-4xl text-white font-light mb-3 leading-tight">
                Einen Koch
              </h2>

              <p className="inline-block px-3 py-1 mb-4 text-[10px] uppercase tracking-[0.2em] font-semibold text-black bg-[#c4a35a]">
                70 % Teilzeit
              </p>

              <p className="text-white/50 text-sm leading-relaxed mb-5 max-w-sm mx-auto md:mx-0">
                Werde Teil unseres Landgut-Teams! Einsatztage: Donnerstag, Freitag und Samstag.
              </p>

              <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-6">
                {['Donnerstag', 'Freitag', 'Samstag'].map((day) => (
                  <div
                    key={day}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg"
                  >
                    <Calendar className="w-3 h-3 text-brand-400" />
                    <span className="text-white/80 text-[10px] uppercase tracking-wider">{day}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row justify-center md:justify-start gap-3">
                <a href="mailto:info@hueckels-may.de?subject=Bewerbung%20Koch">
                  <button
                    type="button"
                    className="w-full sm:w-auto px-6 py-3 bg-white text-black text-xs uppercase tracking-[0.2em] font-bold rounded-full hover:bg-brand-800 hover:text-white active:scale-[0.98] transition-all duration-200 inline-flex items-center justify-center gap-2"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    Jetzt bewerben
                  </button>
                </a>
                <Link to="/jobs">
                  <button
                    type="button"
                    className="w-full sm:w-auto px-6 py-3 border border-white/20 text-white text-xs uppercase tracking-[0.2em] font-bold rounded-full hover:bg-white/5 active:scale-[0.98] transition-all duration-200 inline-flex items-center justify-center gap-2"
                  >
                    Alle Jobs
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </Link>
              </div>

              <p className="mt-4 text-white/35 text-xs">
                Bewerbungen unter{' '}
                <a
                  href="mailto:info@hueckels-may.de?subject=Bewerbung%20Koch"
                  className="text-brand-400 hover:text-brand-300 transition-colors underline underline-offset-4"
                >
                  info@hueckels-may.de
                </a>
              </p>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          CTA SECTION - Final Push
      ════════════════════════════════════════════════════════════ */}
      <section className="content-auto py-12 md:py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-6">Bereit?</p>
            
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl text-white font-light mb-8 leading-tight">
              Lassen Sie uns Ihr<br />
              <span className="italic text-white/60">nächstes Event</span> planen
            </h2>
            
            <p className="text-white/40 text-lg md:text-xl mb-12 max-w-2xl mx-auto">
              Erzählen Sie uns von Ihrer Vision. Wir freuen uns darauf, gemeinsam mit Ihnen etwas Besonderes zu erschaffen.
            </p>

            <Link to="/kontakt">
              <button
                type="button"
                className="px-8 sm:px-10 md:px-14 py-4 md:py-5 bg-white text-black text-sm uppercase tracking-[0.3em] font-bold rounded-full hover:bg-brand-800 hover:text-white active:scale-[0.98] transition-all duration-200"
              >
                Kontakt aufnehmen
              </button>
            </Link>
          </motion.div>
        </div>
      </section>

    </div>
  )
}
